/**
 * Indonesian render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'id')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const indonesianLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'blur' },
    change: { primary: 'ubah' },
    click: { primary: 'klik' },
    dblclick: { primary: 'dblclick' },
    focus: { primary: 'fokus' },
    input: { primary: 'masukan' },
    keydown: { primary: 'tekan_tombol' },
    keypress: { primary: 'keypress' },
    keyup: { primary: 'keyup' },
    load: { primary: 'muat' },
    mousedown: { primary: 'mousedown' },
    mouseenter: { primary: 'mouseenter' },
    mouseleave: { primary: 'mouseleave' },
    mousemove: { primary: 'mousemove' },
    mouseout: { primary: 'tinggalkan' },
    mouseover: { primary: 'arahkan' },
    mouseup: { primary: 'mouseup' },
    reset: { primary: 'reset' },
    resize: { primary: 'ubah_ukuran' },
    scroll: { primary: 'gulir' },
    submit: { primary: 'kirim' },
    touchcancel: { primary: 'touchcancel' },
    touchend: { primary: 'touchend' },
    touchmove: { primary: 'touchmove' },
    touchstart: { primary: 'touchstart' },
    unload: { primary: 'bongkar' },
  },
  logical: {
    and: { primary: 'dan' },
    changes: { primary: 'berubah' },
    contains: { primary: 'berisi' },
    else: { primary: 'lainnya' },
    end: { primary: 'akhir' },
    equals: { primary: 'sama' },
    exists: { primary: 'ada' },
    has: { primary: 'punya' },
    have: { primary: 'punya' },
    includes: { primary: 'termasuk' },
    is: { primary: 'adalah' },
    live: { primary: 'langsung' },
    matches: { primary: 'cocok' },
    not: { primary: 'bukan' },
    or: { primary: 'atau' },
    otherwise: { primary: 'sebaliknya' },
    then: { primary: 'lalu' },
    when: { primary: 'ketika' },
    where: { primary: 'di_mana' },
  },
  temporal: {
    forever: { primary: 'selamanya' },
    h: { primary: 'j' },
    hour: { primary: 'jam' },
    hours: { primary: 'jam' },
    millisecond: { primary: 'milidetik' },
    milliseconds: { primary: 'milidetik' },
    min: { primary: 'mnt' },
    minute: { primary: 'menit' },
    minutes: { primary: 'menit' },
    ms: { primary: 'ms' },
    s: { primary: 'd' },
    second: { primary: 'detik' },
    seconds: { primary: 'detik' },
    times: { primary: 'kali' },
  },
  values: {
    body: { primary: 'badan' },
    detail: { primary: 'detail' },
    document: { primary: 'dokumen' },
    element: { primary: 'elemen' },
    event: { primary: 'peristiwa' },
    false: { primary: 'salah' },
    it: { primary: 'itu' },
    its: { primary: 'miliknya' },
    me: { primary: 'saya' },
    my: { primary: 'saya punya' },
    myself: { primary: 'saya sendiri' },
    null: { primary: 'null' },
    result: { primary: 'hasil' },
    target: { primary: 'target' },
    true: { primary: 'benar' },
    undefined: { primary: 'tidak_terdefinisi' },
    value: { primary: 'nilai' },
    window: { primary: 'jendela' },
    you: { primary: 'kamu' },
    your: { primary: 'kamu punya' },
    yourself: { primary: 'kamu sendiri' },
  },
  attributes: {
    attribute: { primary: 'atribut' },
    attributes: { primary: 'atribut_atribut' },
    class: { primary: 'kelas' },
    classes: { primary: 'kelas_kelas' },
    properties: { primary: 'properti_properti' },
    property: { primary: 'properti' },
    style: { primary: 'gaya' },
    styles: { primary: 'gaya_gaya' },
  },
  expressions: {
    up: { primary: 'atas' },
    down: { primary: 'bawah' },
    left: { primary: 'kiri' },
    right: { primary: 'kanan' },
    at: { primary: 'di' },
    back: { primary: 'mundur' },
    characters: { primary: 'karakter' },
    children: { primary: 'anak_anak' },
    closest: { primary: 'terdekat' },
    elsewhere: { primary: 'luaran' },
    empty: { primary: 'kosong' },
    'ends with': { primary: 'diakhiri dengan' },
    exclusive: { primary: 'eksklusif' },
    first: { primary: 'pertama' },
    'ignoring case': { primary: 'abaikan kapital' },
    inclusive: { primary: 'inklusif' },
    index: { primary: 'indeks' },
    'joined by': { primary: 'digabung oleh' },
    last: { primary: 'terakhir' },
    'mapped to': { primary: 'dipetakan ke' },
    next: { primary: 'berikutnya' },
    no: { primary: 'tidak_ada' },
    parent: { primary: 'induk' },
    prev: { primary: 'sblm' },
    previous: { primary: 'sebelumnya' },
    random: { primary: 'acak' },
    some: { primary: 'beberapa' },
    'sorted by': { primary: 'diurutkan berdasarkan' },
    'split by': { primary: 'dipisah oleh' },
    start: { primary: 'memulai' },
    catch: { primary: 'menangkap' },
    finally: { primary: 'akhirnya' },
    def: { primary: 'fungsi' },
    'starts with': { primary: 'dimulai dengan' },
    within: { primary: 'dalam' },
  },
};

registerLexicon('id', indonesianLexicon);
